import cx from 'classnames';
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { getDateRangeText } from '@culture/common-helpers/date';

import { catalogsSelectors } from 'ducks/catalogs';
import CustomSelect from 'components/filters/CustomSelect';
import Button from 'components/Button';
import Calendar from '../Calendar';

import ApplyAndClearBlock from '../blocks/ApplyAndClearBlock';
import SeasonsButtonsBlock from '../blocks/SeasonsButtonsBlock';
import styles from './styles.module.scss';

const DesktopEventsControls = ({
	filters,
	onFilterChange,
	onDatesChange,
	filtersQueryParams
}) => {
	const [isShowPreciseFilters, setIsShowPreciseFilters] = useState(false);

	const filtersValues = useSelector(catalogsSelectors.selectFilterValues);
	const filtersLoading = useSelector(catalogsSelectors.selectFiltersLoading);

	const { startDate, endDate } = filters;

	const seasonsClassNameSetter = (condition) =>
		cx(styles.DesktopEventsControls__Item, {
			[styles.DesktopEventsControls__Item_active]: condition
		});
	const openPreciseFilters = () => {
		setIsShowPreciseFilters(true);
	};
	const closePreciseFilters = () => {
		setIsShowPreciseFilters(false);
	};

	// eslint-disable-next-line react/prop-types
	const CalendarComponent = ({ callback, currentDates = {} }) => {
		const [currentDate, setCurrentDate] = useState(currentDates);

		const onHandleApply = () => {
			const isOneDay = !currentDate.endDate;
			const newDates = isOneDay
				? { ...currentDate, endDate: currentDate.startDate }
				: currentDate;

			onDatesChange(newDates);
			callback();
		};
		const onHandleClear = () => {
			onDatesChange({});
			callback();
		};

		return (
			<>
				<Calendar
					startDate={startDate}
					endDate={endDate}
					setNewDates={setCurrentDate}
				/>
				<div className={styles.CalendarApplyAndClearBlock}>
					<ApplyAndClearBlock onApply={onHandleApply} onClear={onHandleClear} />
				</div>
			</>
		);
	};

	const renderCalendarWithCloseModalCallback = (callback) => (
		<CalendarComponent
			{...{ callback, currentDates: { startDate, endDate } }}
		/>
	);

	const calendarTitle = useMemo(
		() =>
			getDateRangeText({ endDate, startDate }, 'd MMMM yyyy').replace(
				'–',
				' – '
			) || 'Выберите дату',
		[startDate, endDate]
	);

	if (isShowPreciseFilters) {
		return (
			<div className={styles.DesktopEventsControls}>
				<div className={styles.DesktopEventsControls__ControlsBlock}>
					<div className={styles.DesktopEventsControls__Item}>
						<Button icon="close" width="square" onClick={closePreciseFilters} />
					</div>
					<div className={styles.DesktopEventsControls__Item}>
						<CustomSelect
							name="locale"
							selectedValue={filters.locale}
							options={filtersValues.locales}
							isLoading={filtersLoading}
							onChange={onFilterChange}
							resetOption={{
								title: 'По всей России',
								sysName: 'russia',
								titleGenitive: 'России'
							}}
							apiParams={{
								url: 'catalogFilters/afisha/locales',
								debounce: 500,
								affectQueryParams: {
									...filtersQueryParams,
									isMuseumExhibition: true
								}
							}}
							controlTitleField="title"
							label="Местоположение"
							valueField="sysName"
							accent
							style={{
								fontWeight: 'bold'
							}}
							searchable
							hasClearButton
						/>
					</div>

					<div className={styles.DesktopEventsControls__Item}>
						<CustomSelect
							name="date"
							resetOption={{
								title: calendarTitle
							}}
							isLoading={filtersLoading}
							onChange={onFilterChange}
							style={{
								fontWeight: 'bold'
							}}
							customChildren={renderCalendarWithCloseModalCallback}
						/>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={styles.DesktopEventsControls}>
			<div
				className={cx(
					styles.DesktopEventsControls__ControlsBlock,
					styles.DesktopEventsControls__ControlsBlock_seasonsMode
				)}
			>
				<div className={styles.DesktopEventsControls__Item}>
					<Button
						icon="filtersButton"
						width="square"
						className={styles.DesktopEventsControls__ShowHideButton}
						onClick={openPreciseFilters}
					/>
				</div>
				<SeasonsButtonsBlock
					setPeriod={onDatesChange}
					currentSeason={filters?.season}
					classNameSetter={seasonsClassNameSetter}
				/>
			</div>
		</div>
	);
};

DesktopEventsControls.propTypes = {
	onFilterChange: PropTypes.func,
	onDatesChange: PropTypes.func,
	onPriceChange: PropTypes.func,
	onFilterReset: PropTypes.func,
	filters: PropTypes.shape({
		season: PropTypes.string,
		locale: PropTypes.string,
		startDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
		endDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
		query: PropTypes.string
	}),
	defaultFiltersValues: PropTypes.object,
	filtersQueryParams: PropTypes.object
};

export default DesktopEventsControls;
